import React, { useState } from 'react';
import { AlertTriangle, CalendarCheck, CalendarClock, ChevronRight, Loader2 } from 'lucide-react';
import { auth } from '../../shared/api/firebase/config';
import { ModuleHeader } from '../../components/ui/RdaUI';

const API_BASE =
  import.meta.env.VITE_RDA_API_BASE_URL ||
  import.meta.env.VITE_BACKEND_BASE_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_BACKEND_URL ||
  'https://rda-backend-thuq.onrender.com';

const getAuthHeaders = async () => {
  try {
    const token = await getToken();
    return token
      ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      : { 'Content-Type': 'application/json' };
  } catch {
    return { 'Content-Type': 'application/json' };
  }
};

const REGIME_OPTIONS = [
  { value: 'CPC', label: 'CPC — Código de Processo Civil (úteis)' },
  { value: 'JEC', label: 'JEC — Juizados Especiais Cíveis (úteis)' },
  { value: 'CLT', label: 'CLT — Consolidação das Leis do Trabalho (úteis)' },
  { value: 'CPP', label: 'CPP — Código de Processo Penal (corridos)' },
  { value: 'MATERIAL', label: 'MATERIAL — Direito Material (corridos)' },
];

const REGIME_INFO = {
  CPC: 'Contagem em dias úteis forenses. Recesso (20/12–20/01) suspende o prazo.',
  JEC: 'Contagem em dias úteis forenses. Recesso (20/12–20/01) suspende o prazo.',
  CLT: 'Contagem em dias úteis forenses. Recesso (20/12–20/01) suspende o prazo.',
  CPP: 'Contagem em dias corridos. Recesso não suspende o prazo.',
  MATERIAL: 'Contagem em dias corridos. Recesso não suspende o prazo.',
};

const formatDateBR = (isoString) => {
  if (!isoString) return '';
  const [year, month, day] = isoString.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const getDayOfWeek = (isoString) => {
  if (!isoString) return '';
  const [year, month, day] = isoString.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('pt-BR', { weekday: 'long' });
};

const getDaysUntil = (isoString) => {
  if (!isoString) return null;
  const [year, month, day] = isoString.split('-').map(Number);
  const target = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
  return diff;
};

export default function DeadlineCalculator() {
  const today = new Date().toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(today);
  const [nDays, setNDays] = useState(15);
  const [regime, setRegime] = useState('CPC');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleCalculate = async () => {
    setError('');
    setResult(null);
    setLoading(true);

    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${API_BASE}/deadlines/calculate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          start_date: startDate,
          n_days: Number(nDays),
          regime,
        }),
      });

      if (resp.status === 401 || resp.status === 403) {
        setError('Sessão expirada ou não autorizada. Faça login novamente.');
        return;
      }

      if (resp.status === 503) {
        setError('Motor de prazos indisponível no servidor. Verifique se o backend está rodando.');
        return;
      }

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setError(data?.detail || `Erro ${resp.status}: falha ao calcular o prazo.`);
        return;
      }

      const data = await resp.json();
      setResult(data);
    } catch (err) {
      setError('Falha de Conexão: Não foi possível comunicar com os servidores RDA na Nuvem. O documento será retido na fila.');
    } finally {
      setLoading(false);
    }
  };

  const daysUntil = result ? getDaysUntil(result.dies_ad_quem) : null;
  const isUrgent = daysUntil !== null && daysUntil >= 0 && daysUntil <= 3;
  const isExpired = daysUntil !== null && daysUntil < 0;

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-gray-50 p-6 gap-6">

      

      <div className="flex flex-col xl:flex-row gap-6 items-start">

        {/* FORMULÁRIO */}
        <div className="w-full xl:max-w-md bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col gap-5">

          {/* Data de Publicação */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold uppercase tracking-widest text-gray-500">
              Data da Publicação / Intimação
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-[#20243B] font-medium focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]"
            />
            <p className="text-[10px] text-gray-400">O dies a quo (início da contagem) será o próximo dia útil após esta data.</p>
          </div>

          {/* Prazo em Dias */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold uppercase tracking-widest text-gray-500">
              Prazo em Dias
            </label>
            <input
              type="number"
              min={1}
              max={3650}
              value={nDays}
              onChange={(e) => setNDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-[#20243B] font-medium focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]"
            />
          </div>

          {/* Regime */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-bold uppercase tracking-widest text-gray-500">
              Regime Processual
            </label>
            <select
              value={regime}
              onChange={(e) => setRegime(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-[#20243B] font-medium focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059] bg-white"
            >
              {REGIME_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400">{REGIME_INFO[regime]}</p>
          </div>

          {/* Botão */}
          <button
            type="button"
            onClick={handleCalculate}
            disabled={loading || !startDate || nDays < 1}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm uppercase tracking-widest transition-all ${
              loading || !startDate || nDays < 1
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-[#20243B] text-[#C5A059] hover:bg-[#2d3350] shadow-md hover:shadow-lg'
            }`}
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Calculando...</>
            ) : (
              <><CalendarCheck className="w-4 h-4" /> Calcular Prazo Fatal</>
            )}
          </button>

          {/* Erro */}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-700 font-medium">{error}</p>
            </div>
          )}
        </div>

        {/* RESULTADO */}
        {result && (
          <div className="w-full flex flex-col gap-4">

            {/* Dies ad Quem — Destaque principal */}
            <div className={`rounded-2xl border-2 shadow-lg p-6 flex flex-col gap-2 ${
              isExpired
                ? 'border-red-400 bg-red-50'
                : isUrgent
                ? 'border-amber-400 bg-amber-50'
                : 'border-[#C5A059] bg-[#20243B]'
            }`}>
              <p className={`text-[11px] font-bold uppercase tracking-widest ${
                isExpired ? 'text-red-500' : isUrgent ? 'text-amber-600' : 'text-[#C5A059]'
              }`}>
                {isExpired ? '⚠️ Prazo Vencido' : isUrgent ? '⚡ Prazo Urgente' : '✅ Dies ad Quem — Prazo Fatal'}
              </p>

              <p className={`text-3xl font-extrabold leading-tight ${
                isExpired ? 'text-red-700' : isUrgent ? 'text-amber-800' : 'text-white'
              }`}>
                {formatDateBR(result.dies_ad_quem)}
              </p>

              <p className={`text-sm font-medium capitalize ${
                isExpired ? 'text-red-500' : isUrgent ? 'text-amber-600' : 'text-[#C5A059]/80'
              }`}>
                {getDayOfWeek(result.dies_ad_quem)}
              </p>

              {daysUntil !== null && (
                <p className={`text-xs font-bold mt-1 ${
                  isExpired ? 'text-red-500' : isUrgent ? 'text-amber-700' : 'text-gray-300'
                }`}>
                  {isExpired
                    ? `Venceu há ${Math.abs(daysUntil)} dia${Math.abs(daysUntil) !== 1 ? 's' : ''}`
                    : daysUntil === 0
                    ? 'Vence hoje!'
                    : `Faltam ${daysUntil} dia${daysUntil !== 1 ? 's' : ''}`}
                </p>
              )}
            </div>

            {/* Badges de informação */}
            <div className="flex flex-wrap gap-2">
              {result.was_extended && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[11px] font-bold">
                  <CalendarClock className="w-3 h-3" />
                  Prorrogado — caiu em Feriado/FDS
                </span>
              )}
              {result.recess_days_skipped > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 border border-blue-300 text-blue-800 text-[11px] font-bold">
                  <CalendarClock className="w-3 h-3" />
                  {result.recess_days_skipped} dia{result.recess_days_skipped !== 1 ? 's' : ''} de recesso pulado{result.recess_days_skipped !== 1 ? 's' : ''}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-300 text-gray-700 text-[11px] font-bold">
                {result.regime} · {result.n_days} dia{result.n_days !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Detalhes da contagem */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col gap-3">
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Detalhamento da Contagem</p>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-xs text-gray-500 font-medium">Data do Evento / Publicação</span>
                  <span className="text-xs font-bold text-[#20243B]">{formatDateBR(result.start_date)}</span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-[#C5A059]" />
                    <span className="text-xs text-gray-500 font-medium">Dies a Quo (início da contagem)</span>
                  </div>
                  <span className="text-xs font-bold text-[#20243B]">{formatDateBR(result.dies_a_quo)}</span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-[#C5A059]" />
                    <span className="text-xs text-gray-500 font-medium">Prazo ({result.n_days} dias {['CPC','JEC','CLT'].includes(result.regime) ? 'úteis' : 'corridos'})</span>
                  </div>
                  <span className="text-xs font-bold text-[#20243B]">{result.n_days}d</span>
                </div>

                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-[#C5A059]" />
                    <span className="text-xs text-gray-500 font-medium">Dies ad Quem (prazo fatal)</span>
                  </div>
                  <span className="text-xs font-bold text-emerald-700">{formatDateBR(result.dies_ad_quem)}</span>
                </div>
              </div>
            </div>

            {/* Aviso Legal */}
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-amber-700">
                Este cálculo é uma ferramenta de apoio. Confirme sempre com o diário oficial e o andamento processual. Feriados municipais podem variar por ano.
              </p>
            </div>
          </div>
        )}

        {/* Estado vazio */}
        {!result && !loading && !error && (
          <div className="w-full flex flex-col items-center justify-center py-16 gap-3 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
              <CalendarClock className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-sm font-bold text-gray-400">Aguardando cálculo</p>
            <p className="text-xs text-gray-300 max-w-xs">
              Preencha os campos ao lado e clique em "Calcular Prazo Fatal" para obter o dies ad quem.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
